// ─── Plan Features ─────────────────────────────────────────────────────────────

export const getPlanFeatures = (plan) => {
  const baseFeatures = [
    `${plan?.max_users || "Unlimited"} Users`,
    "Shared Team Inbox",
    "Basic Reports",
    "Email Support",
  ];

  switch (plan?.name) {
    case "whatsapp_lite":
    case "engage":
      return [
        ...baseFeatures,
        "WhatsApp Channel Only",
        "1K messages/hour",
        "3 Agent Logins",
      ];
    case "whatsapp_advanced":
    case "automate":
      return [
        ...baseFeatures,
        "WhatsApp Channel",
        "10K messages/hour",
        "5 Agent Logins",
        "1 Rule-Based Chatbot",
        "Advanced Reports",
      ];
    case "whatsapp_advanced_custom_solution":
    case "orchestrate":
      return [
        ...baseFeatures,
        "WhatsApp + 1 Additional Channel",
        "Unlimited messages/hour",
        "10 Agent Logins",
        "3 Rule-Based Chatbots",
        "Custom Integrations",
        "Priority Support",
      ];
    case "enterprise":
      return [
        ...baseFeatures,
        "All Channels Enabled",
        "Unlimited messages/hour",
        "Unlimited Agent Logins",
        "5+ Rule-Based Chatbots",
        "Custom Broadcast Speed",
        "Enterprise Reports",
        "Extended Integrations",
        "Dedicated Support",
      ];
    case "elite":
      return [
        "Marketing broadcasts for IAP program and new product launches",
        "Basic chatbot for investors to register requests and capture leads",
        "Live agent features where businesses can respond to investor communication",
        "Chatbots - Existing Investor, Lead Generation and Automated Birthday wishes",
        "One-click Upload of Files from Redvision or InvestWell.",
        "Send requested reports via WhatsApp chatbot.",
        "Run ads and marketing campaigns on any platform and connect them to the WhatsApp chatbot.",
        "Goal planning, financial calculators, and other value-add tools.",
        "Completely customised for IFAs, Mutual Fund Distributors, Wealth Managers, etc."
      ]
    case "pro":
      return [
        "All features in Elite.",
        "Custom chatbot flows and journeys.",
        "Custom integrations and reports as per requirements.",
        "Priority onboarding and support.",
        "Full Custom Solution as per requirements."
      ]
    default:
      return baseFeatures;
  }
};

// ─── Billing Cycle Config ──────────────────────────────────────────────────────

// Canonical order used when sorting or displaying billing cycles.
export const BILLING_CYCLE_ORDER = ["monthly", "quarterly", "annual"];

export const BILLING_CYCLE_CONFIG = {
  monthly:   { label: "Monthly",   suffix: "/month",   periodLabel: "per month",   months: 1  },
  quarterly: { label: "Quarterly", suffix: "/quarter", periodLabel: "per quarter", months: 3  },
  annual:    { label: "Annual",    suffix: "/year",    periodLabel: "per year",    months: 12 },
};

// ─── Billing Cycle Helpers ─────────────────────────────────────────────────────

/**
 * Normalize a billing cycle string to a BILLING_CYCLE_CONFIG key.
 * "yearly" → "annual"; everything else is lowercased.
 * Returns null when cycle is absent so callers can supply their own fallback
 * rather than silently defaulting to an arbitrary cycle.
 */
export const normalizeCycle = (cycle) => {
  if (!cycle) return null;
  const lower = cycle.toLowerCase();
  return lower === "yearly" ? "annual" : lower;
};

export const denormalizeCycle = (cycle) => {
  if (!cycle) return null;
  const lower = cycle.toLowerCase();
  return lower === "annual" ? "yearly" : lower;
};

export const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case "USD": return "$";
    case "EUR": return "€";
    case "GBP": return "£";
    case "INR": return "₹";
    case "AED": return "د.إ";
    default:    return "₹";
  }
};

// ─── Plan Pricing Helpers ──────────────────────────────────────────────────────

/**
 * Find the active pricing entry for a given billing cycle from a plan's pricing
 * array. Normalizes both keys before comparing. Returns null if not found.
 */
export const getPlanPricing = (plan, billingCycle) => {
  if (!plan?.pricing?.length || !billingCycle) return null;
  const normalized = normalizeCycle(billingCycle);
  return (
    plan.pricing.find(
      (p) => p.is_active && normalizeCycle(p.billing_cycle) === normalized
    ) || null
  );
};

/**
 * Get the numeric price for a plan at a given billing cycle.
 * Returns 0 if no matching active pricing entry exists.
 */
export const getPlanPrice = (plan, billingCycle) => {
  const entry = getPlanPricing(plan, billingCycle);
  return entry ? parseFloat(entry.price) || 0 : 0;
};

/**
 * Return the sorted unique billing-cycle keys that have at least one active
 * pricing entry across the provided plans array.
 * Order follows BILLING_CYCLE_ORDER (monthly → quarterly → annual).
 */
export const getAvailableBillingCycles = (plans) => {
  const found = new Set();
  (plans || []).forEach((plan) => {
    (plan.pricing || []).forEach((p) => {
      if (p.is_active) {
        const key = normalizeCycle(p.billing_cycle);
        if (key && BILLING_CYCLE_CONFIG[key]) found.add(key);
      }
    });
  });
  return BILLING_CYCLE_ORDER.filter((c) => found.has(c));
};

// ─── Display Helpers ───────────────────────────────────────────────────────────

/**
 * Format a price value with the correct currency symbol.
 * @param {number|string|null} price
 * @param {string} [currencyCode] defaults to 'INR'
 */
export const formatPrice = (price, currencyCode) => {
  const sym = getCurrencySymbol(currencyCode);
  if (price == null || price === 0 || price === '0') return `${sym}0`;
  return `${sym}${parseFloat(price).toLocaleString('en-IN')}`;
};

/**
 * Format an ISO date string to a localised human-readable date.
 */
export const formatDate = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

/**
 * Return a human-readable display name for an addon.
 * Accepts either a name string or an addon object ({ name, displayName }).
 * Falls back to title-casing the snake_case name for unmapped addons.
 */
export const getAddonDisplayName = (nameOrAddon) => {
  const name = typeof nameOrAddon === 'string'
    ? nameOrAddon
    : (nameOrAddon?.displayName || nameOrAddon?.name);
  if (!name) return 'Add-on';
  const nameMap = {
    additional_agents:       '👤 Additional Agents',
    additional_chatbots:     '🤖 Additional Chatbots',
    custom_reports:          '📊 Custom Reports',
    additional_channel:      '📱 Additional Channel',
    whatsapp_numbers:        '📞 WhatsApp Numbers',
    ecommerce_package:       '🛒 Ecommerce Package',
    additional_integrations: '🔌 Additional Integrations',
    onboarding_fee:          'Onboarding Fee',
  };
  return nameMap[name] || name.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};
